/* eslint-disable import/extensions */
/* eslint-disable import/no-unresolved */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useHistory } from 'react-router-dom';
import { Alert, AlertDescription, Button } from '@jem-open/jem-ui';
import { Loader2 } from 'lucide-react';
import { environment } from '../../../environment/environment';
import EducationQuestionCard from '../../../components/EducationQuestionCard';
import { EmptyState } from '../../../components/EmptyState';
import { Pager } from '../../../components/pager';

const PAGE_SIZE = 10;

/**
 * @summary EducationQuestions component
 *
 * @component
 */
const EducationQuestions = () => {
  const history = useHistory();
  const [questions, setQuestions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [numberOfPages, setNumberOfPages] = useState(0);
  const latestRequestId = useRef(0);

  const getEducationQuestions = useCallback((pageNumber) => {
    const requestId = latestRequestId.current + 1;
    latestRequestId.current = requestId;
    setIsLoading(true);
    setError('');
    axios
      .get(
        `${environment.baseUrl}/questions?page_size=${PAGE_SIZE}&page_number=${pageNumber}&platform=all`,
      )
      .then((res) => {
        if (requestId !== latestRequestId.current) return;
        setQuestions(res.data.questions || []);
        setCurrentPage(res.data.current_page || pageNumber);
        setNumberOfPages(res.data.total_number_of_pages || 1);
        setIsLoading(false);
      })
      .catch((err) => {
        if (requestId !== latestRequestId.current) return;
        setError(err.message || 'An error occurred');
        setIsLoading(false);
      });
  }, []);

  useEffect(() => {
    const INITIAL_PAGE_NUMBER = 1;
    getEducationQuestions(INITIAL_PAGE_NUMBER);
  }, [getEducationQuestions]);

  return (
    <div className="min-h-screen bg-white p-md">
      <div
        className="-mx-md -mt-md mb-md flex flex-wrap items-center justify-between gap-sm px-md py-lg"
        style={{ backgroundColor: 'rgba(237,244,251,0.5)' }}>
        <div>
          <h1 className="m-none font-heading text-2xl font-semibold text-greyscale-text-title">
            Education Questions
          </h1>
          <p className="m-none mt-xxs max-w-2xl text-sm text-greyscale-text-body">
            Manage chatbot education questions and their answers.
          </p>
        </div>
        <Button
          variant="primary"
          onClick={() => history.push('/admin/question/add')}>
          Add New
        </Button>
      </div>

      {error && (
        <Alert variant="destructive" className="mb-md">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {isLoading && (
        <div className="flex justify-center py-xl">
          <Loader2 className="h-6 w-6 animate-spin text-secondary-text-label" />
        </div>
      )}

      {!isLoading && !error && questions.length === 0 && (
        <EmptyState
          icon="fas fa-question-circle"
          message="No education questions found"
        />
      )}

      {!isLoading && questions.length > 0 && (
        <div className="mx-auto w-full max-w-4xl">
          {questions.map((question) => (
            <EducationQuestionCard
              key={question.id}
              questionId={question.id}
              template={question.template}
              dateAdded={question.date_created}
              addedBy={question.added_by}
              dateModified={question.date_modified}
              modifiedBy={question.modified_by}
              answers={question.answers}
              platform={question.platform}
              isActive={question.is_active}
              category={question.category}
              questionType={question.question_type}
            />
          ))}
        </div>
      )}

      {!isLoading && numberOfPages > 1 && (
        <div className="mt-md flex justify-center">
          <Pager
            currentPage={currentPage}
            totalPages={numberOfPages}
            onPageChange={getEducationQuestions}
          />
        </div>
      )}
    </div>
  );
};

export default EducationQuestions;
